import axios from 'axios';

// URL base do backend
const API_URL = process.env.REACT_APP_API_URL || 'https://repflow.cloud/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000, // 15 segundos (importante por causa do "sono" do Render no plano gratuito)
});

// Timeout estendido para chamadas de extração via IA (PDF/imagem) — passar como
// { timeout: TIMEOUT_EXTRACAO_IA } no config da chamada, não altera o default da instância.
// Coerente com o backend: relatórios extensos podem acionar 1 retry interno com
// max_tokens maior (services/extracao_relatorio_comissao.py — _TIMEOUT_REQUEST_SEGUNDOS
// = 240s por tentativa, até 2 tentativas = 480s no pior caso). Sempre que um dos dois
// timeouts mudar, ajuste o outro para manter a folga.
export const TIMEOUT_EXTRACAO_IA = 540000; // 9 minutos (folga de ~1min sobre o pior caso do backend)

// ============================================================
// Cold Start — polling enquanto o servidor acorda
// ============================================================

// Guard de módulo: impede múltiplas instâncias de cold start simultâneas.
// Requisições concorrentes que falham ao mesmo tempo (ex: Dashboard carrega 5 endpoints)
// compartilham a mesma coldStartPromise e só disparam um cold start.
let isColdStarting = false;
let coldStartPromise = null;

const waitForServer = () =>
  new Promise((resolve, reject) => {
    const MAX_WAIT_MS = 90_000; // 90 segundos
    const POLL_INTERVAL_MS = 5_000; // tenta a cada 5 segundos
    const startedAt = Date.now();

    const poll = async () => {
      try {
        // Usa axios diretamente (não a instância api) para não re-acionar o interceptor
        await axios.get(`${API_URL}/health`, { timeout: 8_000 });
        resolve();
      } catch {
        if (Date.now() - startedAt >= MAX_WAIT_MS) {
          reject(new Error('O servidor não acordou dentro do tempo limite.'));
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    // Primeira tentativa imediata — o servidor pode já estar acordando
    poll();
  });

// ============================================================
// Interceptor de REQUEST — injeta token em todas as chamadas
// ============================================================
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================================
// Interceptor de RESPONSE — trata erros globalmente + cold start
// ============================================================
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const isCanceled =
      error.code === 'ERR_CANCELED' || error.message === 'canceled';

    // Erro de rede: sem resposta do servidor (inclui timeout ECONNABORTED e ERR_NETWORK)
    const isNetworkError = !error.response && !isCanceled;

    // Flag que marcamos na config antes de re-tentar — evita loop infinito
    const isRetry = error.config?._coldStartRetry === true;

    // ── Cold Start ──────────────────────────────────────────
    // Dispara apenas em erro de rede (servidor dormindo ou sem resposta),
    // nunca em retentativa, e nunca em requisição cancelada.
    if (isNetworkError && !isRetry) {
      // Apenas o primeiro request cria o processo de polling;
      // os demais aguardam a mesma promise.
      if (!isColdStarting) {
        isColdStarting = true;
        window.dispatchEvent(new CustomEvent('repflow:coldstart:start'));

        coldStartPromise = waitForServer().finally(() => {
          window.dispatchEvent(new CustomEvent('repflow:coldstart:end'));
          isColdStarting = false;
          coldStartPromise = null;
        });
      }

      try {
        // Aguarda o servidor acordar (promise compartilhada entre requests)
        await coldStartPromise;

        // Servidor respondeu — re-tenta a requisição original uma única vez
        error.config._coldStartRetry = true;
        return api(error.config);
      } catch {
        // Servidor não acordou em 90s — deixa cair para o tratamento padrão
      }
    }

    // ── Tratamento padrão de erros HTTP ─────────────────────
    if (error.response) {
      const { status } = error.response;

      // Token expirado ou inválido — desloga automaticamente
      if (status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }

      // Sem permissão
      if (status === 403) {
        console.warn('Acesso negado:', error.response.data?.detail);
      }

      // Erro interno do servidor
      if (status >= 500) {
        console.error('Erro no servidor:', error.response.data);
      }
    } else if (error.code === 'ECONNABORTED') {
      console.error('Timeout: o servidor demorou para responder. Tente novamente.');
    } else if (!error.response && !isCanceled) {
      console.error('Sem conexão com o servidor. Verifique sua internet.');
    }

    return Promise.reject(error);
  }
);

export default api;
